
import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { Building, User, Users, Target, Loader2 } from "lucide-react";

interface StartupProfileData {
  startup_name: string;
  website_link: string;
  about_founder: string;
  co_founder: string;
  about_startup: string;
  startup_stage: string;
  expectations_from_teenvisor: string;
}

interface StartupProfileFormProps {
  onProfileComplete: () => void;
}

const StartupProfileForm: React.FC<StartupProfileFormProps> = ({ onProfileComplete }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState<StartupProfileData>({
    startup_name: "",
    website_link: "",
    about_founder: "",
    co_founder: "",
    about_startup: "",
    startup_stage: "",
    expectations_from_teenvisor: ""
  });

  const handleInputChange = (field: keyof StartupProfileData, value: string) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to create a startup profile",
        variant: "destructive"
      });
      return;
    }

    if (!profileData.startup_name.trim() || !profileData.about_startup.trim()) {
      toast({
        title: "Missing Information",
        description: "Please fill in at least the startup name and about startup fields",
        variant: "destructive"
      });
      return;
    }

    try {
      setLoading(true);

      const { error } = await supabase
        .from("startup_accounts")
        .insert({
          user_id: user.id,
          startup_name: profileData.startup_name.trim(),
          bio: profileData.about_startup.trim(),
          website_url: profileData.website_link.trim() || null,
          status: 'approved' // Auto-approve for now
        });

      if (error) {
        console.error("Error creating startup profile:", error);
        throw error;
      }

      toast({
        title: "Profile Created",
        description: "Your startup profile has been created successfully!"
      });

      onProfileComplete();
    } catch (error) {
      console.error("Error creating startup profile:", error);
      toast({
        title: "Error",
        description: "Failed to create startup profile. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-4 flex items-center justify-center gap-2">
            <Building className="h-8 w-8 text-primary" />
            Complete Your Startup Profile
          </h1>
          <p className="text-muted-foreground">
            Tell us about your startup to get the most out of Teenvisor
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building className="h-5 w-5" />
                  Startup Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="startup_name">Startup Name *</Label>
                  <Input
                    id="startup_name"
                    value={profileData.startup_name}
                    onChange={(e) => handleInputChange('startup_name', e.target.value)}
                    placeholder="Enter your startup name"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="website_link">Website Link</Label>
                  <Input
                    id="website_link"
                    type="url"
                    value={profileData.website_link}
                    onChange={(e) => handleInputChange('website_link', e.target.value)}
                    placeholder="https://your-startup.com"
                  />
                </div>
                <div>
                  <Label htmlFor="startup_stage">Startup Stage</Label>
                  <Select onValueChange={(value) => handleInputChange('startup_stage', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select your startup stage" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="idea">Idea Stage</SelectItem>
                      <SelectItem value="mvp">MVP/Prototype</SelectItem>
                      <SelectItem value="early">Early Stage</SelectItem>
                      <SelectItem value="growth">Growth Stage</SelectItem>
                      <SelectItem value="scale">Scale Stage</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Founder Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="about_founder">About Founder</Label>
                  <Textarea
                    id="about_founder"
                    value={profileData.about_founder}
                    onChange={(e) => handleInputChange('about_founder', e.target.value)}
                    placeholder="Tell us about yourself as a founder"
                    rows={3}
                  />
                </div>
                <div>
                  <Label htmlFor="co_founder">Co-Founder(s)</Label>
                  <Textarea
                    id="co_founder"
                    value={profileData.co_founder}
                    onChange={(e) => handleInputChange('co_founder', e.target.value)}
                    placeholder="Tell us about your co-founder(s) if any"
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                About Your Startup
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="about_startup">About Startup *</Label>
                <Textarea
                  id="about_startup"
                  value={profileData.about_startup}
                  onChange={(e) => handleInputChange('about_startup', e.target.value)}
                  placeholder="Describe your startup, what problem you're solving, and your vision"
                  rows={4}
                  required
                />
              </div>
              <div>
                <Label htmlFor="expectations_from_teenvisor">What do you expect from Teenvisor?</Label>
                <Textarea
                  id="expectations_from_teenvisor"
                  value={profileData.expectations_from_teenvisor}
                  onChange={(e) => handleInputChange('expectations_from_teenvisor', e.target.value)}
                  placeholder="Tell us how you plan to use Teenvisor and what you hope to achieve"
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-center">
            <Button 
              type="submit" 
              size="lg" 
              disabled={loading}
              className="w-full max-w-md"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Complete Profile & Access Dashboard
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StartupProfileForm;
